import { supabase } from '../lib/supabaseClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Team Chat Service
 * Real-time messaging for organization members
 */

export interface TeamMessage {
  id: string;
  created_at: string;
  organization_id: string;
  user_id: string;
  content: string;
  sender_name: string;
  sender_email: string;
  image_url?: string | null;  // Optional image attachment
}

/**
 * Send a message to the team chat
 * @param content - Message text content
 * @param imageUrl - Optional image URL from storage
 */
export const sendMessage = async (content: string, imageUrl?: string): Promise<TeamMessage> => {
  console.log('[TeamChat] 📤 Sending message:', { content: content.substring(0, 50) + '...' });
  
  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get user's organization
  const { data: membership, error: membershipError } = await supabase
    .from('organization_members')
    .select('organization_id')
    .eq('user_id', user.id)
    .eq('is_active', true)
    .single();

  if (membershipError || !membership) {
    console.error('[TeamChat] ❌ No organization membership found:', membershipError);
    throw new Error('No organization found');
  }

  // Get user's display name
  const firstName = user.user_metadata?.first_name || '';
  const lastName = user.user_metadata?.last_name || '';
  const senderName = `${firstName} ${lastName}`.trim() || user.email || 'Anonymous';

  // Insert message
  const messageData: any = {
    organization_id: membership.organization_id,
    user_id: user.id,
    content,
    sender_name: senderName,
    sender_email: user.email || ''
  };
  
  // Add image URL if provided
  if (imageUrl) {
    messageData.image_url = imageUrl;
    console.log('[TeamChat] 📷 Attaching image:', imageUrl);
  }
  
  const { data, error } = await supabase
    .from('team_messages')
    .insert(messageData)
    .select()
    .single();

  if (error) {
    console.error('[TeamChat] ❌ Failed to send message:', error);
    throw error;
  }

  console.log('[TeamChat] ✅ Message sent successfully:', data.id);
  
  // Trigger push notification to team members (async, don't wait)
  triggerTeamNotification(membership.organization_id, user.id, senderName, content).catch(err => {
    console.error('[TeamChat] Failed to send push notification:', err);
  });
  
  return data;
};

/**
 * Trigger push notification to team members (via backend)
 */
async function triggerTeamNotification(organizationId: string, senderId: string, senderName: string, messagePreview: string): Promise<void> {
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    if (!apiBaseUrl) {
      console.warn('[TeamChat] No API base URL configured for push notifications');
      return;
    }

    const preview = messagePreview.length > 50 ? messagePreview.substring(0, 50) + '...' : messagePreview;
    
    await fetch(`${apiBaseUrl}/api/push/team`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organizationId,
        excludeUserId: senderId,
        title: `${senderName} sent a message`,
        body: preview,
        type: 'team_chat',
        data: { senderId, senderName }
      })
    });
    
    console.log('[TeamChat] 📱 Push notification triggered');
  } catch (error) {
    console.error('[TeamChat] Error triggering push notification:', error);
  }
}

/**
 * Get message history for the user's organization
 */
export const getMessages = async (limit: number = 100): Promise<TeamMessage[]> => {
  console.log('[TeamChat] 📥 Fetching messages...');
  
  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get user's organization
  const { data: membership, error: membershipError } = await supabase
    .from('organization_members')
    .select('organization_id')
    .eq('user_id', user.id)
    .eq('is_active', true)
    .single();

  if (membershipError || !membership) {
    console.error('[TeamChat] ❌ No organization membership found:', membershipError);
    throw new Error('No organization found');
  }

  // Fetch messages
  const { data, error } = await supabase
    .from('team_messages')
    .select('*')
    .eq('organization_id', membership.organization_id)
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('[TeamChat] ❌ Failed to fetch messages:', error);
    throw error;
  }

  console.log('[TeamChat] ✅ Fetched', data.length, 'messages');
  return data || [];
};

/**
 * Subscribe to real-time message updates
 */
export const subscribeToMessages = (
  onNewMessage: (message: TeamMessage) => void,
  onError?: (error: Error) => void
): Promise<RealtimeChannel | null> => {
  return new Promise(async (resolve) => {
    console.log('[TeamChat] 🔌 Setting up real-time subscription...');
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[TeamChat] ❌ Not authenticated for subscription');
        resolve(null);
        return;
      }

      // Get user's organization
      const { data: membership, error: membershipError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (membershipError || !membership) {
        console.error('[TeamChat] ❌ No organization for subscription:', membershipError);
        resolve(null);
        return;
      }

      // Create subscription channel
      const channel = supabase
        .channel(`team_messages:${membership.organization_id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'team_messages',
            filter: `organization_id=eq.${membership.organization_id}`
          },
          (payload) => {
            console.log('[TeamChat] 📨 New message received:', payload.new);
            onNewMessage(payload.new as TeamMessage);
          }
        )
        .subscribe((status) => {
          console.log('[TeamChat] 🔌 Subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('[TeamChat] ✅ Real-time subscription active');
          }
        });

      resolve(channel);
    } catch (error) {
      console.error('[TeamChat] ❌ Subscription error:', error);
      if (onError) onError(error as Error);
      resolve(null);
    }
  });
};

/**
 * Unsubscribe from real-time updates
 */
export const unsubscribeFromMessages = async (channel: RealtimeChannel): Promise<void> => {
  console.log('[TeamChat] 🔌 Unsubscribing from real-time updates...');
  await supabase.removeChannel(channel);
  console.log('[TeamChat] ✅ Unsubscribed');
};
